import { client } from '@/services/api/axios.ts';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

export type JobStatusInfo = 'ready' | 'processing' | 'failed' | 'partial';

export type TimeSeriesPoint = {
  time: string;
  value: number;
};

type TradingOpportunityTimelineResponse = {
  forecast: TimeSeriesPoint[];
  actualLoad: TimeSeriesPoint[];
};

type PriceDeltaHeatmapResponse = {
  energyPrices: TimeSeriesPoint[];
  batteryAvailableEnergy: {
    availableEnergy: number;
    stateOfCharge: number;
    time: string;
  }[];
};

type HistoricalPatternVsCurrentPricesResponse = Record<string, never>;

type ChartResponseMap = {
  'trading-opportunity-timeline': TradingOpportunityTimelineResponse;
  'price-delta-heatmap': PriceDeltaHeatmapResponse;
  'historical-pattern-vs-current-prices': HistoricalPatternVsCurrentPricesResponse;
};

type ChartTradingResponse<T extends keyof ChartResponseMap> = {
  body: ChartResponseMap[T];
  status: JobStatusInfo;
  message: string;
};

export type ChartType = keyof ChartResponseMap;

interface UseGetChartTradingQueryParams<T extends ChartType> {
  date: Date;
  type: T;
}

export const useGetChartTradingQuery = <T extends ChartType>({
  date,
  type,
}: UseGetChartTradingQueryParams<T>) => {
  const formattedDate = format(date, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['trading', 'chart', type, formattedDate],
    queryFn: async () => {
      const { data } = await client.get<ChartTradingResponse<T>>(`/trading/chart`, {
        params: { date: formattedDate, type },
      });

      return {
        data: data.body,
        status: data.status,
        message: data.message,
      };
    },
    retry: 1,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 30,
  });
};
